import {
  type CancelApprovalCommand,
  type CreateGroupBuyDailyReconciliationApprovalCommand,
  type CreateGroupBuyReconciliationResolutionApprovalCommand,
  type CreateInventoryOperationApprovalCommand,
  type CreateMemberBalanceRefundApprovalCommand,
  type CreateOrderCashRefundApprovalCommand,
  type CreatePaidOrderItemReturnApprovalCommand,
  type CreatePurchaseOrderApprovalCommand,
  type DecideApprovalCommand,
  isCancelApprovalCommand,
  isCreateGroupBuyDailyReconciliationApprovalCommand,
  isCreateGroupBuyReconciliationResolutionApprovalCommand,
  isCreateInventoryOperationApprovalCommand,
  isCreateMemberBalanceRefundApprovalCommand,
  isCreateOrderCashRefundApprovalCommand,
  isCreatePaidOrderItemReturnApprovalCommand,
  isCreatePurchaseOrderApprovalCommand,
  isDecideApprovalCommand,
} from "@billiard/contracts";

export type ApprovalMutationCommand =
  | {
      kind: "CREATE_ORDER_CASH_REFUND";
      request: CreateOrderCashRefundApprovalCommand;
    }
  | {
      kind: "CREATE_MEMBER_BALANCE_REFUND";
      request: CreateMemberBalanceRefundApprovalCommand;
    }
  | {
      kind: "CREATE_PAID_ORDER_ITEM_RETURN";
      request: CreatePaidOrderItemReturnApprovalCommand;
    }
  | {
      kind: "CREATE_INVENTORY_OPERATION";
      request: CreateInventoryOperationApprovalCommand;
    }
  | {
      kind: "CREATE_PURCHASE_ORDER_APPROVAL";
      request: CreatePurchaseOrderApprovalCommand;
    }
  | {
      kind: "CREATE_GROUP_BUY_DAILY_RECONCILIATION";
      request: CreateGroupBuyDailyReconciliationApprovalCommand;
    }
  | {
      kind: "CREATE_GROUP_BUY_RECONCILIATION_RESOLUTION";
      request: CreateGroupBuyReconciliationResolutionApprovalCommand;
    }
  | { kind: "DECIDE"; request: DecideApprovalCommand }
  | { kind: "CANCEL"; request: CancelApprovalCommand };

export interface ApprovalMutationRecovery {
  ownerIdentityKey: string;
  command: ApprovalMutationCommand;
  state: "CLAIMED" | "OUTCOME_UNKNOWN";
}

export type ApprovalMutationRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: ApprovalMutationRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

export const APPROVAL_MUTATION_RECOVERY_STORAGE_KEY = "billiard-erp:approval-mutation-recovery:v1";

let pending: ApprovalMutationRecovery | null = null;
let dispatching: ApprovalMutationRecovery | null = null;
let hydrated = false;
let unavailable = false;

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function bounded(value: unknown, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    [...value].length <= maximum
  );
}

export function approvalMutationIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function validIdentityKey(value: unknown): value is string {
  if (!bounded(value, 1_024)) return false;
  try {
    const parts = JSON.parse(value) as unknown;
    return Array.isArray(parts) && parts.length === 3 && parts.every((part) => bounded(part, 512));
  } catch {
    return false;
  }
}

function validCommand(value: unknown): value is ApprovalMutationCommand {
  if (!record(value) || !exactKeys(value, ["kind", "request"])) return false;
  switch (value.kind) {
    case "CREATE_ORDER_CASH_REFUND":
      return isCreateOrderCashRefundApprovalCommand(value.request);
    case "CREATE_MEMBER_BALANCE_REFUND":
      return isCreateMemberBalanceRefundApprovalCommand(value.request);
    case "CREATE_PAID_ORDER_ITEM_RETURN":
      return isCreatePaidOrderItemReturnApprovalCommand(value.request);
    case "CREATE_INVENTORY_OPERATION":
      return isCreateInventoryOperationApprovalCommand(value.request);
    case "CREATE_PURCHASE_ORDER_APPROVAL":
      return isCreatePurchaseOrderApprovalCommand(value.request);
    case "CREATE_GROUP_BUY_DAILY_RECONCILIATION":
      return isCreateGroupBuyDailyReconciliationApprovalCommand(value.request);
    case "CREATE_GROUP_BUY_RECONCILIATION_RESOLUTION":
      return isCreateGroupBuyReconciliationResolutionApprovalCommand(value.request);
    case "DECIDE":
      return isDecideApprovalCommand(value.request);
    case "CANCEL":
      return isCancelApprovalCommand(value.request);
    default:
      return false;
  }
}

function frozen(
  ownerIdentityKey: string,
  command: ApprovalMutationCommand,
  state: ApprovalMutationRecovery["state"],
): ApprovalMutationRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function parsed(value: unknown): ApprovalMutationRecovery | null {
  if (
    !record(value) ||
    !exactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !record(value.recovery)
  ) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !exactKeys(recovery, ["ownerIdentityKey", "command", "state"]) ||
    !validIdentityKey(recovery.ownerIdentityKey) ||
    !validCommand(recovery.command) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return frozen(recovery.ownerIdentityKey, recovery.command, recovery.state);
}

function same(left: ApprovalMutationRecovery, right: ApprovalMutationRecovery): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function readStored(target: Storage): ApprovalMutationRecovery | null {
  const encoded = target.getItem(APPROVAL_MUTATION_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : parsed(JSON.parse(encoded) as unknown);
}

function save(recovery: ApprovalMutationRecovery): boolean {
  if (typeof window === "undefined") return true;
  const target = storage();
  if (!target) return false;
  try {
    target.setItem(
      APPROVAL_MUTATION_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStored(target);
    return stored !== null && same(stored, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const target = storage();
  if (!target) {
    unavailable = true;
    return;
  }
  try {
    const encoded = target.getItem(APPROVAL_MUTATION_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const stored = readStored(target);
    if (!stored) {
      unavailable = true;
      return;
    }
    const recovery =
      stored.state === "CLAIMED"
        ? frozen(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !save(recovery)) {
      unavailable = true;
      return;
    }
    pending = recovery;
  } catch {
    unavailable = true;
  }
}

export function readApprovalMutationRecovery(identityKey: string): ApprovalMutationRecoveryView {
  hydrate();
  if (unavailable) return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  if (!pending) return { status: "NONE" };
  return pending.ownerIdentityKey === identityKey
    ? { status: "OWNED", recovery: pending }
    : { status: "OTHER_OPERATOR" };
}

export function claimApprovalMutationRecovery(
  identityKey: string,
  command: ApprovalMutationCommand,
): ApprovalMutationRecoveryView {
  const current = readApprovalMutationRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!validIdentityKey(identityKey) || !validCommand(command)) {
    unavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = frozen(identityKey, command, "CLAIMED");
  if (!save(recovery)) {
    unavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pending = recovery;
  return { status: "OWNED", recovery };
}

function exactDurable(recovery: ApprovalMutationRecovery, identityKey: string): boolean {
  hydrate();
  if (
    unavailable ||
    pending !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    (typeof window !== "undefined" && !storage())
  ) {
    unavailable = true;
    return false;
  }
  if (typeof window === "undefined") return true;
  try {
    const target = storage();
    const stored = target ? readStored(target) : null;
    if (stored && same(stored, recovery)) return true;
  } catch {
    // Fall through to the fail-closed result.
  }
  unavailable = true;
  return false;
}

export function beginApprovalMutationDispatch(
  recovery: ApprovalMutationRecovery,
  identityKey: string,
): boolean {
  if (dispatching || !exactDurable(recovery, identityKey)) return false;
  dispatching = recovery;
  return true;
}

export function finishApprovalMutationDispatch(
  recovery: ApprovalMutationRecovery,
  identityKey: string,
): boolean {
  if (dispatching !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatching = null;
  return true;
}

export function markApprovalMutationOutcomeUnknown(
  recovery: ApprovalMutationRecovery,
  identityKey: string,
): boolean {
  if (!exactDurable(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : frozen(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!save(unknown)) {
    unavailable = true;
    return false;
  }
  pending = unknown;
  return true;
}

export function clearApprovalMutationRecovery(
  recovery: ApprovalMutationRecovery,
  identityKey: string,
): boolean {
  if (!exactDurable(recovery, identityKey)) return false;
  if (typeof window !== "undefined") {
    const target = storage();
    if (!target) {
      unavailable = true;
      return false;
    }
    try {
      target.removeItem(APPROVAL_MUTATION_RECOVERY_STORAGE_KEY);
      if (target.getItem(APPROVAL_MUTATION_RECOVERY_STORAGE_KEY) !== null) {
        unavailable = true;
        return false;
      }
    } catch {
      unavailable = true;
      return false;
    }
  }
  pending = null;
  return true;
}

export function approvalMutationOutcomeIsUnknown(error: unknown): boolean {
  const candidate = error as { code?: unknown; statusCode?: unknown } | null;
  const code = typeof candidate?.code === "string" ? candidate.code : null;
  const statusCode = typeof candidate?.statusCode === "number" ? candidate.statusCode : null;
  if (
    code === "GATEWAY_UNREACHABLE" ||
    code === "GATEWAY_RESPONSE_INVALID" ||
    code === "GROUP_BUY_DAILY_RECONCILIATION_APPROVAL_RESULT_UNKNOWN" ||
    code === "GROUP_BUY_DAILY_RECONCILIATION_RECOVERY_AMBIGUOUS" ||
    code === "GROUP_BUY_RECONCILIATION_RESOLUTION_APPROVAL_RESULT_UNKNOWN" ||
    code === "GROUP_BUY_RECONCILIATION_RESOLUTION_RECOVERY_AMBIGUOUS" ||
    code === "OPERATOR_IDENTITY_CHANGED"
  ) {
    return true;
  }
  if (statusCode === null) return true;
  return statusCode === 408 || statusCode === 429 || statusCode >= 500;
}

export function resetApprovalMutationRecoveryForTests(): void {
  pending = null;
  dispatching = null;
  hydrated = false;
  unavailable = false;
  try {
    storage()?.removeItem(APPROVAL_MUTATION_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup resets the in-memory fail-closed state as well.
  }
}

export function resetApprovalMutationRecoveryMemoryForTests(): void {
  pending = null;
  dispatching = null;
  hydrated = false;
  unavailable = false;
}
